import { type JSX, Show } from 'solid-js';

// The dashboard's one on/off control (#5625, spec §3.9): a real
// `<button type="button" role="switch" aria-checked>`, so Space and Enter are
// native button activation and the state is one attribute a screen reader reads.
//
// CONTROLLED, never self-flipping: a click asks the caller to act
// (`onActivate`) and `aria-checked` only ever follows `checked`. The paint comes
// from the caller's re-read of the thing it toggles, not from the click's guess
// (`lib/agent-mode-save.ts`'s no-optimistic-paint rule), so a refused write
// leaves the switch where the server says it is.
//
// The button holds a track and a thumb and no text. The accessible name is the
// caller's (`aria-label`, or `aria-labelledby` when a visible label already
// names it); `stateLabel` is the visible On / Off beside it.
//
// `disabled` IS `aria-disabled`, NEVER THE ATTRIBUTE (#6755). A caller marks the
// switch disabled for exactly the window its write is in flight, and that write
// starts while the switch is the focused control — the confirm dialog has just
// handed focus back to it. Chromium's focus fixup blurs a focused element the
// frame it becomes `disabled`, so the keyboard user who had just confirmed was
// left on <body>: not announced, Shift+Tab unable to return, and on the agent
// modes panel the next Tab restarted at the top of the list. `aria-disabled`
// keeps the node in the tab order; the click handler is what refuses the
// activation, because `aria-disabled` is presentational and a click or Space
// still reaches it. Same shape `Pager` settled on in #5264;
// `.switch[aria-disabled='true']` paints the dimmed, not-allowed look.

export type SwitchProps = {
  readonly checked: boolean;
  /** Busy or locked: still focusable, activation ignored. See the docblock. */
  readonly disabled?: boolean;
  /** The click. The Switch never flips its own state; repaint from the re-read. */
  readonly onActivate: () => void;
  /** The visible state text beside the button ("On" / "Off"). */
  readonly stateLabel?: string;
  readonly 'aria-label'?: string;
  readonly 'aria-labelledby'?: string;
};

export function Switch(props: SwitchProps): JSX.Element {
  return (
    <span class="switch-field">
      <button
        type="button"
        role="switch"
        class="switch"
        aria-checked={props.checked}
        aria-label={props['aria-label']}
        aria-labelledby={props['aria-labelledby']}
        aria-disabled={props.disabled === true ? 'true' : undefined}
        onClick={() => {
          if (props.disabled === true) return;
          props.onActivate();
        }}
      >
        <span class="switch__track" />
        <span class="switch__thumb" />
      </button>
      <Show when={props.stateLabel}>{(label) => <span class="switch__state">{label()}</span>}</Show>
    </span>
  );
}
